"use client";

import { type ComponentPropsWithoutRef, type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import type { PluggableList } from "unified";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema, type Options as Schema } from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import type { MarkdownBlock, MarkdownDocument, MarkdownHeading } from "@/lib/content/markdown";
import { typographyStyles } from "@/lib/theme/tokens";
import { cn } from "@/lib/utils";

import { MarkdownCodeBlock } from "./markdown-code-block";
import { MarkdownMermaidBlock } from "./markdown-mermaid-block";

type MarkdownVariant = "default" | "inline";
type HeadingLevel = MarkdownHeading["depth"];
type HeadingTagName = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

type MarkdownContentProps = {
  document?: MarkdownDocument | null;
  className?: string;
  emptyState?: ReactNode;
  variant?: MarkdownVariant;
};

type HeadingBlock = Extract<MarkdownBlock, { type: "heading" }>;

type VariantConfig = {
  container: string;
  paragraph: string;
  unorderedList: string;
  orderedList: string;
  listItem: string;
  blockquote: string;
  inlineCode: string;
  taskListItem: string;
  tableWrapper: string;
};

type MarkdownListItemProps = ComponentPropsWithoutRef<"li"> & {
  node?: unknown;
  checked?: boolean;
};

type MarkdownCodeProps = ComponentPropsWithoutRef<"code"> & {
  node?: unknown;
  inline?: boolean;
};

type MarkdownImageProps = ComponentPropsWithoutRef<"img"> & {
  node?: unknown;
};

const ALLOWED_HTML_ELEMENTS = [
  "details",
  "summary",
  "br",
  "img",
  "div",
  "span",
  "sup",
  "sub",
  "kbd",
] as const;

const SAFE_HTML_SCHEMA = createSafeHtmlSchema();
const MARKDOWN_REHYPE_PLUGINS: PluggableList = [rehypeRaw, [rehypeSanitize, SAFE_HTML_SCHEMA]];

const VARIANT_STYLES: Record<MarkdownVariant, VariantConfig> = {
  default: {
    container: "space-y-6",
    paragraph: "text-muted-foreground",
    unorderedList: "list-disc space-y-2 pl-6 text-foreground marker:text-foreground",
    orderedList: "list-decimal space-y-2 pl-6 text-foreground marker:text-foreground",
    listItem: "leading-relaxed",
    blockquote:
      "rounded-[var(--radius-xl)] border-l-2 border-border/70 bg-muted/20 px-4 py-3 text-muted-foreground",
    inlineCode: "rounded-[var(--radius-sm)] bg-muted px-1 py-0.5 font-mono text-[13px] text-foreground",
    taskListItem: "pl-0 list-none",
    tableWrapper: "overflow-x-auto rounded-[var(--radius-xl)] border border-border/60",
  },
  inline: {
    container: "space-y-3 text-sm leading-relaxed",
    paragraph: "text-current",
    unorderedList: "list-disc space-y-1 pl-5 text-current marker:text-current/60",
    orderedList: "list-decimal space-y-1 pl-5 text-current marker:text-current/60",
    listItem: "leading-relaxed text-current",
    blockquote: "border-l-2 border-current/40 pl-3 text-current/80",
    inlineCode: "rounded-[var(--radius-sm)] bg-muted/60 px-1 py-0.5 font-mono text-[13px]",
    taskListItem: "pl-0 list-none text-current",
    tableWrapper: "overflow-x-auto",
  },
};

export function MarkdownContent({
  document,
  className,
  emptyState,
  variant = "default",
}: MarkdownContentProps) {
  const content = document?.source ?? "";
  const hasBlocks = (document?.blocks.length ?? 0) > 0;

  if (!document || (!hasBlocks && !content.trim())) {
    return (
      emptyState ?? (
        <div className="rounded-[var(--radius-xl)] border border-dashed border-border/70 bg-muted/20 p-6 text-sm text-muted-foreground">
          No content available.
        </div>
      )
    );
  }

  const headingResolver = createHeadingResolver(
    document.blocks.filter((block): block is HeadingBlock => block.type === "heading"),
  );
  const components = createMarkdownComponents(variant, headingResolver);
  const containerClass = cn(
    "markdown-content min-w-0 break-words",
    variant === "inline" && "markdown-content--inline",
    VARIANT_STYLES[variant].container,
    className,
  );

  return (
    <div className={containerClass}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

function createHeadingResolver(headings: HeadingBlock[]) {
  let cursor = 0;
  return (level: HeadingLevel) => {
    for (let index = cursor; index < headings.length; index += 1) {
      const candidate = headings[index];
      if (candidate.level === level) {
        cursor = index + 1;
        return candidate.id;
      }
    }
    return undefined;
  };
}

function createMarkdownComponents(
  variant: MarkdownVariant,
  headingResolver: (level: HeadingLevel) => string | undefined,
): Components {
  const variantStyles = VARIANT_STYLES[variant];

  const renderHeading = (level: HeadingLevel) => {
    const HeadingComponent = (
      props: ComponentPropsWithoutRef<"h1"> & { node?: unknown },
    ) => {
      const { node, children, style, className, ...rest } = props;
      void node;
      const headingStyle =
        level === 1
          ? typographyStyles.h2
          : level === 2
            ? typographyStyles.h3
            : typographyStyles.h4;
      const id = headingResolver(level);

      return (
        <TagForLevel
          level={level}
          id={id}
          className={cn(
            "scroll-mt-24 text-foreground",
            variant === "inline" && "text-current",
            className,
          )}
          style={{ ...headingStyle, ...(style ?? {}) }}
          {...rest}
        >
          {children}
        </TagForLevel>
      );
    };
    HeadingComponent.displayName = `MarkdownHeadingLevel${level}`;
    return HeadingComponent;
  };

  return {
    h1: renderHeading(1),
    h2: renderHeading(2),
    h3: renderHeading(3),
    h4: renderHeading(4),
    h5: renderHeading(5),
    h6: renderHeading(6),
    p: (props) => {
      const { node, children, style, className, ...rest } = props;
      void node;
      return (
        <p
          {...rest}
          style={{ ...typographyStyles.body, ...(style ?? {}) }}
          className={cn(variantStyles.paragraph, className)}
        >
          {children}
        </p>
      );
    },
    a: (props) => {
      const { node, href, children, className, ...rest } = props;
      void node;
      const external = isExternalLink(href);
      return (
        <a
          {...rest}
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noreferrer noopener" : undefined}
          className={cn(
            "underline decoration-dotted decoration-foreground/50 underline-offset-4 transition hover:text-foreground",
            className,
          )}
        >
          {children}
        </a>
      );
    },
    ul: (props) => {
      const { node, className, children, ...rest } = props;
      void node;
      return (
        <ul {...rest} className={cn(variantStyles.unorderedList, className)}>
          {children}
        </ul>
      );
    },
    ol: (props) => {
      const { node, className, children, ...rest } = props;
      void node;
      return (
        <ol {...rest} className={cn(variantStyles.orderedList, className)}>
          {children}
        </ol>
      );
    },
    li: (props: MarkdownListItemProps) => {
      const { node, checked, children, className, ...rest } = props;
      void node;
      if (typeof checked === "boolean") {
        return (
          <li {...rest} className={cn(variantStyles.taskListItem, className)}>
            <span className="inline-flex items-start gap-2">
              <input
                type="checkbox"
                checked={checked}
                readOnly
                disabled
                className="mt-1 h-4 w-4 rounded border border-border/70 bg-background text-primary"
                aria-hidden
              />
              <span>{children}</span>
            </span>
          </li>
        );
      }
      return (
        <li {...rest} className={cn(variantStyles.listItem, className)}>
          {children}
        </li>
      );
    },
    blockquote: (props) => {
      const { node, className, children, ...rest } = props;
      void node;
      return (
        <blockquote {...rest} className={cn(variantStyles.blockquote, className)}>
          {children}
        </blockquote>
      );
    },
    code: (props: MarkdownCodeProps) => {
      const { node, inline, className, children, ...rest } = props;
      void node;
      const languageMatch = /language-([\w-]+)/u.exec(className ?? "");
      const normalizedLanguage = languageMatch?.[1]?.toLowerCase();
      const rawChildren = String(children ?? "");
      // react-markdown v9+ no longer passes the `inline` prop to code components,
      // so detect inline code by the absence of a fenced language class and any newlines.
      const isInline = inline ?? (!languageMatch && !rawChildren.includes("\n"));
      if (isInline) {
        return (
          <code {...rest} className={cn(variantStyles.inlineCode, className)}>
            {children}
          </code>
        );
      }

      const code = rawChildren.replace(/\n$/, "");
      if (normalizedLanguage === "mermaid") {
        return <MarkdownMermaidBlock code={code} />;
      }
      return <MarkdownCodeBlock code={code} language={languageMatch?.[1]} />;
    },
    table: (props) => {
      const { node, className, children, ...rest } = props;
      void node;
      return (
        <div
          className={cn(variantStyles.tableWrapper, "-mx-1 max-w-full px-1")}
          role="region"
          aria-label="Markdown table"
        >
          <table
            {...rest}
            className={cn("w-full border-collapse text-sm", className)}
          >
            {children}
          </table>
        </div>
      );
    },
    img: (props: MarkdownImageProps) => {
      const { node, className, alt, loading, decoding, children, ...rest } = props;
      void node;
      void children;
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          {...rest}
          alt={alt ?? ""}
          loading={loading ?? "lazy"}
          decoding={decoding ?? "async"}
          className={cn(
            "h-auto max-w-full rounded-[var(--radius-lg)] border border-border/40 bg-muted/10",
            className,
          )}
        />
      );
    },
  };
}

function isExternalLink(href?: string | null) {
  if (!href) {
    return false;
  }
  return /^(?:[a-z][a-z0-9+.-]*:)?\/\//iu.test(href);
}

function TagForLevel({
  level,
  className,
  children,
  ...rest
}: ComponentPropsWithoutRef<"h1"> & { level: HeadingLevel }) {
  const tagName = HEADING_TAG_BY_LEVEL[level] ?? "h6";
  const HeadingTag = tagName as HeadingTagName;
  return (
    <HeadingTag className={className} {...rest}>
      {children}
    </HeadingTag>
  );
}

const HEADING_TAG_BY_LEVEL: Record<HeadingLevel, HeadingTagName> = {
  1: "h1",
  2: "h2",
  3: "h3",
  4: "h4",
  5: "h5",
  6: "h6",
};

type AttributeList = NonNullable<Schema["attributes"]>[string];

function createSafeHtmlSchema(): Schema {
  const schema: Schema = {
    ...defaultSchema,
    tagNames: mergeTagNames(defaultSchema.tagNames, ALLOWED_HTML_ELEMENTS),
    attributes: {
      ...(defaultSchema.attributes ?? {}),
    },
  };

  schema.attributes ??= {};
  schema.attributes.details = mergeAttributeAllowlist(schema.attributes.details, ["open"]);
  schema.attributes.img = mergeAttributeAllowlist(schema.attributes.img, [
    "src",
    "srcSet",
    "sizes",
    "alt",
    "title",
    "width",
    "height",
    "loading",
    "decoding",
    "referrerPolicy",
  ]);

  return schema;
}

function mergeTagNames(existing: Schema["tagNames"], additions: readonly string[]) {
  return Array.from(new Set([...(existing ?? []), ...additions]));
}

function mergeAttributeAllowlist(
  existing: AttributeList | undefined,
  additions: readonly string[],
): AttributeList {
  const unique = new Set(existing ?? []);
  additions.forEach((addition) => {
    if (addition) {
      unique.add(addition);
    }
  });
  return Array.from(unique);
}
